import Vue from 'vue'
import JSZip from 'jszip'
import { v4 as uuidv4 } from 'uuid'
import config from '@/configurations/config-environment'
import { BaseService } from '../baseService'
import importDatasetParser from '@/utils/import/importDatasetParser'
import importFormatDetector from '@/utils/import/importFormatDetector'
import importFormatUtil from '@/utils/import/importFormatUtil'
import importLogAdapter from '@/utils/import/importLogAdapter'
import importPayloadUtil from '@/utils/import/importPayloadUtil'

const dataws = config.apis.dataws
const IMPORT_EXPORT_SERVICE = `${dataws}ITWImportExportRestService_V1_0/`

export const DATAWS_ORDER_PREFIX = 'dataws:'

const JOB_STATUS = {
  RUNNING: 102,
  SUCCESS: 200,
  ERROR: 500
}

const CONVERT_SUCCESS = 'Conversion_Succes'
const IMPORT_SUCCESS = 'Import_Succes'

const FIELD_TYPE_OF_IMPORT = 'Import_Field_V1_0_0'
const FIELD_TYPE_OF_FILE = 'Field_XML'
const FIELD_IMPORT_CLASSIFICATION = 'Field_Import'

const XML_ENTRY_PATTERN = /\.(xml|tix)$/i

export class ImportService extends BaseService {
  constructor() {
    super()
    this.jobs = new Map()
  }

  buildRequestEnvelope(useZipStream) {
    const authorisation = this.getAuthorizationObject()

    return {
      Authorisation: authorisation,
      WebserviceVersion: '1.0',
      LanguageCode: this.getLangCodeLongFromShort(authorisation.Language),
      LogFormat: 'JSON',
      UseZipStream: Boolean(useZipStream)
    }
  }

  post(path, body) {
    return Vue.http.post(`${IMPORT_EXPORT_SERVICE}${path}`, body, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  createJob() {
    const orderId = `${DATAWS_ORDER_PREFIX}${uuidv4()}`
    this.jobs.set(orderId, { jobStatus: JOB_STATUS.RUNNING, log: { records: [] } })
    return orderId
  }

  updateJob(orderId, patch) {
    const job = this.jobs.get(orderId) || {}
    this.jobs.set(orderId, { ...job, ...patch })
  }

  failJob(orderId, error) {
    console.error(error)
    this.updateJob(orderId, {
      jobStatus: JOB_STATUS.ERROR,
      log: importLogAdapter.toLogFromJson(error && error.message ? error.message : String(error))
    })
  }

  getJobStatus(orderId) {
    const job = this.jobs.get(orderId)
    if (!job) {
      return Promise.resolve({ jobStatus: JOB_STATUS.ERROR, log: { records: [] } })
    }
    return Promise.resolve(job)
  }

  /* global DecompressionStream */
  async gunzipBase64(value) {
    if (!value) return value

    const binary = atob(value)
    const bytes = Uint8Array.from(binary, (character) => character.charCodeAt(0))
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'))
    return new Response(stream).text()
  }

  async setConvertFile(file) {
    const orderId = this.createJob()

    this.runConvert(orderId, file)

    return orderId
  }

  async runConvert(orderId, file) {
    try {
      const format = file.selectedImportOptions
      const fileName = file.fileDetails.name
      const prepared = await this.prepareFileContent(file)
      const useZipStream = prepared.isBinary

      const body = {
        ...this.buildRequestEnvelope(useZipStream),
        ImportClassification: format.importClassification,
        TypeOfImport: format.typeOfImport,
        Files: [
          {
            FileName: prepared.contentName,
            TypeOfFile: format.typeOfFile,
            TypeOfImport: format.typeOfImport,
            ImportClassification: format.importClassification,
            FileContent: prepared.content,
            Ext: prepared.contentExtension,
            ZipStream: false
          }
        ]
      }

      const response = await this.post('convert', body)
      const result = (response && response.data) || {}

      const conversionLog = useZipStream && result.ConversionLog ? await this.gunzipBase64(result.ConversionLog) : result.ConversionLog
      const log = importLogAdapter.toLogFromJson(conversionLog || result.ResultMessage)

      if (result.ResultCode !== CONVERT_SUCCESS) {
        this.updateJob(orderId, {
          jobStatus: JOB_STATUS.ERROR,
          log: this.withResultMessage(log, result)
        })
        return
      }

      const datasetContent = useZipStream && result.DatasetContent ? await this.gunzipBase64(result.DatasetContent) : result.DatasetContent

      this.updateJob(orderId, {
        jobStatus: JOB_STATUS.SUCCESS,
        log,
        projectFiles: importDatasetParser.parseProjects(datasetContent).map((project) => ({
          ...project,
          filename: fileName
        })),
        datasetContent,
        sourceFieldXml: prepared.sourceFieldXml,
        format,
        fileName,
        contentName: prepared.contentName
      })
    } catch (error) {
      this.failJob(orderId, error)
    }
  }

  async prepareFileContent(file) {
    const format = file.selectedImportOptions
    const fileName = file.fileDetails.name
    const extension = this.getExtension(fileName).toLowerCase()

    if (importFormatUtil.isBinaryContent(format)) {
      return {
        content: file.base64,
        isBinary: true,
        sourceFieldXml: null,
        contentName: fileName,
        contentExtension: extension
      }
    }

    if (extension === '.json') {
      const fieldXml = importPayloadUtil.fieldJsonToXml(await this.readAsText(file))
      return {
        content: fieldXml,
        isBinary: false,
        sourceFieldXml: fieldXml,
        contentName: fileName,
        contentExtension: '.xml'
      }
    }

    if (extension === '.ti') {
      const entry = await this.readFieldXmlFromArchive(file)
      if (entry) {
        return {
          content: entry.content,
          isBinary: false,
          sourceFieldXml: entry.content,
          contentName: entry.name,
          contentExtension: this.getExtension(entry.name) || '.xml'
        }
      }
    }

    const content = await this.readAsText(file)

    return {
      content,
      isBinary: false,
      sourceFieldXml: importFormatDetector.isFieldXml(content) ? content : null,
      contentName: fileName,
      contentExtension: extension
    }
  }

  async readFieldXmlFromArchive(file) {
    try {
      const archive = await JSZip.loadAsync(file.fileDetails)
      const entryNames = Object.keys(archive.files).filter((name) => XML_ENTRY_PATTERN.test(name) && !archive.files[name].dir && !name.startsWith('__MACOSX'))
      if (!entryNames.length) return null

      const entries = await Promise.all(entryNames.map(async (name) => ({ name, content: await archive.file(name).async('string') })))

      return importFormatDetector.pickFieldXmlEntry(entries)
    } catch (error) {
      console.error(error)
      return null
    }
  }

  readAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (event) => resolve(event.target.result)
      reader.onerror = reject
      reader.readAsText(file.fileDetails)
    })
  }

  getExtension(fileName) {
    const index = String(fileName).lastIndexOf('.')
    return index === -1 ? '' : String(fileName).slice(index)
  }

  withResultMessage(log, result) {
    const message = [result.ResultCode, result.ResultMessage].filter(Boolean).join(': ')
    if (!message || (log.records && log.records.length)) return log

    return {
      records: [{ type: importLogAdapter.LOG_TYPE.ERROR, Title: '', parsedMessage: message }]
    }
  }

  async importProject(file, projectFile) {
    const orderId = this.createJob()

    this.runImport(orderId, file, projectFile)

    return orderId
  }

  async runImport(orderId, file, projectFile) {
    try {
      const convertJob = this.jobs.get(file.conversionInformation.orderId)
      const importClassification = file.selectedImportOptions.importClassification
      if (!convertJob || !convertJob.datasetContent) {
        throw new Error('The conversion result is no longer available; please convert the file again.')
      }

      const { path, body } = this.buildImportRequest(convertJob, projectFile, importClassification)
      const response = await this.post(path, body)
      const result = (response && response.data) || {}
      const log = importLogAdapter.toLogFromJson(result.ImportLog || result.ResultMessage)

      this.updateJob(orderId, {
        jobStatus: result.ResultCode === IMPORT_SUCCESS ? JOB_STATUS.SUCCESS : JOB_STATUS.ERROR,
        log: result.ResultCode === IMPORT_SUCCESS ? log : this.withResultMessage(log, result)
      })
    } catch (error) {
      this.failJob(orderId, error)
    }
  }

  buildImportRequest(convertJob, projectFile, importClassification) {
    const envelope = this.buildRequestEnvelope(false)
    importClassification ||= FIELD_IMPORT_CLASSIFICATION

    if (!importPayloadUtil.hasRename(projectFile)) {
      return {
        path: 'import',
        body: {
          ...envelope,
          ImportClassification: importClassification,
          DatasetContent: convertJob.datasetContent
        }
      }
    }

    if (convertJob.sourceFieldXml) {
      const renamed = importPayloadUtil.renameInFieldXml(convertJob.sourceFieldXml, projectFile.projectCode, projectFile.newProjectCode)

      return {
        path: 'convertAndImport',
        body: {
          ...envelope,
          ImportClassification: importClassification,
          TypeOfImport: FIELD_TYPE_OF_IMPORT,
          Parameters: [],
          Files: [
            {
              TypeOfFile: FIELD_TYPE_OF_FILE,
              FileName: convertJob.contentName || convertJob.fileName,
              FileContent: renamed,
              Ext: '.xml',
              ZipStream: false
            }
          ]
        }
      }
    }

    return {
      path: 'import',
      body: {
        ...envelope,
        ImportClassification: importClassification,
        DatasetContent: importPayloadUtil.renameInDataset(convertJob.datasetContent, projectFile.projectCode, projectFile.newProjectCode)
      }
    }
  }
}

export default ImportService
